import React, { useCallback, useRef } from 'react';
import { useTheme } from '@mui/material';
import { useTooltip } from '@visx/tooltip';
import { curveNatural } from '@visx/curve';
import { scaleLinear } from '@visx/scale';
import { LinePath, Line } from '@visx/shape';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { GridRows } from '@visx/grid';
import { localPoint } from '@visx/event';
import TooltipWrapper from './TooltipWrapper';

const margin = { top: 60, right: 0, bottom: 60, left: 40 };

const calculateMaxValue = (data, getYValue) => {
  const values = data.flat()?.map((d) => getYValue(d)) || [];
  return values.length > 0 ? Math.max(...values) * 1.2 : 100;
};

const LineChart = function LineChart({
  width,
  height,
  data = [],
  colors = [],
  getXValue = (d) => d[0],
  getYValue = (d) => d[1],
  xTickFormat = (d) => d,
  yTickFormat = (d) => d,
  maxValue = calculateMaxValue(data, getYValue),
  numTicks = undefined,
  TooltipContent = undefined,
}) {
  const theme = useTheme();
  const svgRef = useRef(null);

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  const normalizedData = Array.isArray(data[0]) ? data : [data];

  const xScale = scaleLinear({
    domain: [
      Math.min(...normalizedData.flat().map((d) => getXValue(d))),
      Math.max(...normalizedData.flat().map((d) => getXValue(d) + 10)),
    ],
    range: [margin.left, xMax],
    nice: false,
  });

  const yScale = scaleLinear({
    domain: [0, maxValue],
    range: [yMax, 0],
    nice: true,
  });

  const {
    tooltipOpen,
    tooltipTop,
    tooltipLeft,
    tooltipData,
    hideTooltip,
    showTooltip,
  } = useTooltip();

  const handleTooltip = useCallback((event) => {
    const { x } = localPoint(event) || { x: 0 };
    const x0 = xScale.invert(x - margin.left); // get Date from the scale

    const index = Math.round(x0);

    // Find the closest data point from all datasets
    const tooltipDataArray = normalizedData.map((dataset) => {
      const d0 = dataset[index - 1];
      const d1 = dataset[index];
      let d = d0;

      if (d1 && getXValue(d1)) {
        d =
          x0.valueOf() - getXValue(d0) > getXValue(d1) - x0.valueOf() ? d1 : d0;
      }

      return d;
    });

    // Get SVG position for portal positioning
    const svgRect = svgRef.current?.getBoundingClientRect() || {
      top: 0,
      left: 0,
    };

    showTooltip({
      tooltipData: tooltipDataArray,
      tooltipLeft: x,
      tooltipTop:
        yScale(getYValue(tooltipDataArray[0])) + margin.top + svgRect.top,
    });
  });

  return (
    <div style={{ position: 'relative' }}>
      <svg ref={svgRef} height={height} width={width}>
        <GridRows
          x1={0}
          top={margin.top - 5}
          left={margin.left + 20}
          scale={yScale}
          numTicks={numTicks?.y}
          stroke={theme.palette.greys.lighter}
          strokeDasharray={5}
          width={xMax}
          height={yMax - margin.top - margin.bottom}
        />
        {normalizedData.map((dataSet, index) => {
          const validData = dataSet.filter(
            (d) => d != null && getXValue(d) != null && getYValue(d) != null
          );
          return (
            <LinePath
              key={String(index)}
              data={validData}
              x={(d) => xScale(getXValue(d)) + 30}
              y={(d) => yScale(getYValue(d)) + margin.top}
              stroke={colors[index]}
              strokeWidth={3}
              curve={curveNatural}
              onMouseLeave={hideTooltip}
            />
          );
        })}
        <AxisBottom
          top={yMax + margin.top}
          left={margin.left + 20}
          hideAxisLine
          hideTicks
          numTicks={numTicks?.x}
          scale={xScale}
          tickFormat={xTickFormat}
          stroke={theme.palette.greys.lighter}
          strokeWidth={1.5}
          tickStroke={theme.palette.greys.lighter}
          tickLabelProps={() => ({
            fill: theme.palette.greys.lighter,
            textAnchor: 'middle',
            dominantBaseline: 'middle',
            fontSize: 14,
            fontFamily: 'Roboto',
          })}
        />
        <AxisLeft
          hideTicks
          hideAxisLine
          numTicks={numTicks?.y}
          top={margin.top}
          left={margin.left}
          scale={yScale}
          tickFormat={yTickFormat}
          tickLabelProps={() => ({
            fill: theme.palette.greys.lighter,
            textAnchor: 'middle',
            fontSize: 14,
            fontFamily: 'Roboto',
            textOverflow: 'ellipsis',
          })}
        />
        <rect
          x={0}
          y={0}
          width={width}
          height={height}
          onTouchStart={handleTooltip}
          fill={'transparent'}
          onTouchMove={handleTooltip}
          onMouseMove={handleTooltip}
          onMouseLeave={() => hideTooltip()}
        />
        {tooltipData && (
          <g>
            <Line
              from={{ x: tooltipLeft, y: 0 }}
              to={{ x: tooltipLeft, y: height }}
              stroke={theme.palette.greys.lighter}
              strokeWidth={2}
              pointerEvents="none"
              strokeDasharray={5}
            />
          </g>
        )}

        {tooltipData &&
          tooltipData.map((d, i) => (
            <g key={String(i)}>
              <circle
                cx={tooltipLeft}
                cy={yScale(d ? getYValue(d) : 0) + margin.top}
                r={5}
                fill={colors[i]}
                stroke={'white'}
                strokeWidth={2}
              />
            </g>
          ))}
      </svg>

      {tooltipData && TooltipContent ? (
        <TooltipWrapper
          open={tooltipOpen}
          tooltipPosition={{ top: tooltipTop, left: tooltipLeft }}
          data={data}
          portal
        >
          <TooltipContent tooltipData={tooltipData} colors={colors} />
        </TooltipWrapper>
      ) : null}
    </div>
  );
};

export default LineChart;
